import { expectsReasoning } from '../../utils/reasoningDisplay';
import { finalizePrivateUpload } from '../../utils/privateStorage';
import { isPremiumChatLocked, PREMIUM_CHAT_LOCK_MESSAGE } from '../../utils/premiumChatAccess';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../../firebase';
import uuid from 'react-native-uuid';
import { getEndpoint, isAnthropic, isGoogle } from '../../chatModels';
import { useModels } from '../../contexts/ModelContext';
import { formatDuration } from '../../utils/formatUtils';
import { createChatOnServer, updateChatTopicOnServer } from '../../utils/chatApi';
import { serializeMessageForBackend } from '../../utils/messagePayload';
import { buildApiUrl } from '../../config/env';
import { captureAnalyticsEvent } from '../../analytics/posthog';
import { applyAssistantStreamMeta } from './streamMeta';
import { consumeSseResponse } from '../../utils/sseStream';

const getAttachmentStorageInfo = (attachment) => {
  const mimeType = attachment.mimeType || '';
  switch (attachment.type) {
    case 'image':
      return { folder: 'chat_images', extension: 'jpg' };
    case 'pdf':
      return { folder: 'chat_pdfs', extension: 'pdf' };
    case 'audio':
      if (mimeType.includes('wav')) {
        return { folder: 'chat_audios', extension: 'wav' };
      }
      if (mimeType.includes('mpeg') || mimeType.includes('mp3')) {
        return { folder: 'chat_audios', extension: 'mp3' };
      }
      return { folder: 'chat_audios', extension: 'audio' };
    case 'video':
      if (mimeType.includes('quicktime')) {
        return { folder: 'chat_videos', extension: 'mov' };
      }
      if (mimeType.includes('webm')) {
        return { folder: 'chat_videos', extension: 'webm' };
      }
      if (mimeType.includes('mpeg')) {
        return { folder: 'chat_videos', extension: 'mpeg' };
      }
      return { folder: 'chat_videos', extension: 'mp4' };
    default:
      return { folder: 'chat_files', extension: 'bin' };
  }
};

const IMAGE_TOOL_NAME = 'image_generation';

const getChatProjectId = (chat = {}) => chat?.projectId || chat?.project_id || null;

const attachProjectId = (requestBody, projectId) => {
  if (requestBody && projectId) {
    requestBody.project_id = projectId;
  }
  return requestBody;
};

export const useMessageSending = ({
  selectedChat,
  selectedModel,
  selectedModelName,
  onMessageSent,
  messages,
  updateChatTitle,
  hasSubscription,
  input,
  setInput,
  setInputText,
  attachments = [],
  activeProjectId = null,
  clearFileState,
  isThinking,
  isDeepResearch,
  setIsDeepResearch,
  reasoningLevel,
  editingContext,
  onEditComplete,
  applyEditAndTruncate
}) => {
  const { models, getModelByModelString, getMaxTokens } = useModels();
  const lastUpdateRef = useRef(0);
  const topicGeneratedChatsRef = useRef(new Set());
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const canSendRef = useRef(null);
  canSendRef.current = () => {
    if (isPremiumChatLocked(selectedChat, messages, models, hasSubscription, [selectedModel])) {
      setError(PREMIUM_CHAT_LOCK_MESSAGE);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (selectedChat?.id && selectedChat?.topic && selectedChat.topic !== 'Untitled Chat') {
      topicGeneratedChatsRef.current.add(selectedChat.id);
    }
  }, [selectedChat]);

  const generateAndUpdateTopic = useCallback(async (responseText, chatId) => {
    if (!chatId || topicGeneratedChatsRef.current.has(chatId)) {
      return;
    }

    const lastUserMessage = messages[messages.length - 2]?.text || '';
    const lastAssistantMessage = responseText;

    const topicPrompt = `
      Based on the following conversation, generate a concise topic of 4-5 words maximum and 30 letters or less:
      User: ${lastUserMessage}
      AI: ${lastAssistantMessage}
      Topic:
    `;

    const authToken = await auth.currentUser.getIdToken();
    const topicResponse = await fetch(
      buildApiUrl('/generate-topic'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ prompt: topicPrompt }),
      }
    );

    const result = await topicResponse.json();
    const generatedTopic = result.topic || 'Untitled Chat';

    try {
      await updateChatTopicOnServer(chatId, generatedTopic);
      topicGeneratedChatsRef.current.add(chatId);
      updateChatTitle(generatedTopic);
    } catch (updateError) {
      console.error('Error updating chat topic:', updateError);
    }
  }, [messages, updateChatTitle]);

  const handleSendMessage = useCallback(async () => {
    if (!canSendRef.current()) return;
    const projectIdForRequest = getChatProjectId(selectedChat) || activeProjectId || null;
    const messageText = input.trim();
    if (!messageText.trim() && attachments.length === 0) return;
    if (!auth.currentUser?.uid) {
      navigate('/welcome');
      return;
    }

    // Clear any previous error
    setError(null);

    captureAnalyticsEvent('message_send_requested', {
      mode: 'single',
      chat_id: selectedChat?.id || null,
      model_string: selectedModel,
      model_name: selectedModelName || selectedModel,
      attachment_count: attachments.length,
      is_thinking: Boolean(isThinking),
      is_deep_research: Boolean(isDeepResearch),
      is_retry: false,
      is_edit: false,
    });

    const modelDetails = getModelByModelString(selectedModel);

    const normalizedAttachments = attachments.map((attachment) => ({
      id: attachment.id,
      type: attachment.type,
      name: attachment.name,
      mimeType: attachment.mimeType,
      size: attachment.size,
      url: attachment.previewUrl || attachment.url,
      previewUrl: attachment.previewUrl || attachment.url,
      isLocal: true,
      uploading: true,
    }));

    const userMessage = {
      id: uuid.v4(),
      text: messageText,
      role: 'user',
      streaming: false,
      pending: true,
      attachments: normalizedAttachments,
    };

    let chatId = selectedChat?.id;
    let backendUserMessagePayload = null;

    // Start async work in parallel so UI can update immediately
    const chatPromise = chatId
      ? Promise.resolve({ chatId })
      : createChatOnServer({
          modelName: selectedModelName || 'GPT 4o mini',
          multiModel: false,
          projectId: projectIdForRequest,
        });

    const geminiAuthTokenPromise = isGoogle(modelDetails) && attachments.length > 0
      ? auth.currentUser.getIdToken()
      : null;
    const attachmentUploadPromise = (attachments.length > 0
      ? Promise.all(attachments.map(async (attachment) => {
          if (!attachment?.file) {
            console.warn('Skipping attachment without file data', attachment);
            return userMessage.attachments.find((item) => item.id === attachment?.id);
          }

          const { folder, extension } = getAttachmentStorageInfo(attachment);
          const storagePath = `${folder}/${auth.currentUser.uid}/${Date.now()}_${attachment.id}.${extension}`;
          const fileRef = ref(storage, storagePath);

          await uploadBytes(fileRef, attachment.file);
          const { url: downloadUrl } = await finalizePrivateUpload(storagePath);

          let uploadedAttachment = {
            ...userMessage.attachments.find((item) => item.id === attachment.id),
            storagePath,
            url: downloadUrl,
            previewUrl: downloadUrl,
            isLocal: false,
            uploading: false,
            mimeType: attachment.mimeType,
          };

          if (geminiAuthTokenPromise) {
            try {
              const authToken = await geminiAuthTokenPromise;
              const response = await fetch(buildApiUrl('/gemini-file-upload'), {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  fileUrl: downloadUrl,
                  fileType: attachment.type
                })
              });

              if (response.ok) {
                const { fileUri, mimeType } = await response.json();
                uploadedAttachment = { ...uploadedAttachment, fileUri, mimeType };
              } else {
                console.error('Failed to process file');
              }
            } catch (error) {
              console.error('Error processing file:', error);
            }
          }

          return uploadedAttachment;
        }))
      : Promise.resolve(null)
    ).then(
      (value) => ({ value, error: null }),
      (error) => ({ value: null, error })
    );

    // Optimistic UI update for existing chats
    if (chatId) {
      onMessageSent({ ...userMessage }, chatId);
      setInput('');
      if (setInputText) {
        setInputText('');
      }
      clearFileState();
      if (isDeepResearch) {
        setIsDeepResearch(false);
      }
    }

    try {
      // Ensure we have a chat id (may come from existing chat or new server creation)
      const chatResult = await chatPromise;
      chatId = chatResult.chatId;

      // For brand new chats, show the user's message as soon as chat id is ready
      if (!selectedChat?.id) {
        captureAnalyticsEvent('chat_created', {
          chat_id: chatId,
          mode: 'single',
          model_string: selectedModel,
          model_name: selectedModelName || selectedModel,
          source: 'first_message',
        });

        onMessageSent({ ...userMessage }, chatId);
        topicGeneratedChatsRef.current.delete(chatId);
        updateChatTitle('Untitled Chat');
        setInput('');
        if (setInputText) {
          setInputText('');
        }
        clearFileState();
        if (isDeepResearch) {
          setIsDeepResearch(false);
        }
      }

      // Handle file uploads (supports multiple attachments)
      const { value: uploadedAttachments, error: uploadError } =
        await attachmentUploadPromise;
      if (uploadError) throw uploadError;
      if (uploadedAttachments) {
        userMessage.attachments = uploadedAttachments.filter(Boolean);
        onMessageSent({ ...userMessage }, chatId);
      }

      backendUserMessagePayload = serializeMessageForBackend(userMessage);

      userMessage.pending = false;
      onMessageSent({ ...userMessage }, chatId);

      // Generate assistant response
      const assistantMessageId = uuid.v4();
      let accumulatedResponse = '';
      let accumulatedReasoning = '';
      let lastCitations = [];
      let isWebSearching = false;
      const conversationHistory = [...messages, userMessage];
      const maxTokens = getMaxTokens(selectedModel);

      const assistantMessage = {
        id: assistantMessageId,
        text: '',
        reasoning_text: '',
        reasoning_pending: expectsReasoning(modelDetails, { isThinking, isDeepResearch, reasoningLevel }),
        reasoning_is_summary: false,
        reasoning_time: '',
        role: 'assistant',
        streaming: true,
        isWebSearching: false,
      };

      const updateMessage = (text, force = false) => {
        const now = Date.now();
        if (force || now - lastUpdateRef.current >= 50) {
          const updatedMessage = {
            ...assistantMessage,
            text: text.trim()
          };
          onMessageSent(updatedMessage, chatId);
          lastUpdateRef.current = now;
          assistantMessage.text = text.trim();
        }
      };

      captureAnalyticsEvent('model_used', {
        mode: 'single',
        chat_id: chatId,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        is_thinking: Boolean(isThinking),
        is_deep_research: Boolean(isDeepResearch),
      });

        let reasoningStartTime = null;
        let reasoningEndTime = null;

        const requestBody = {
          messages: conversationHistory,
          model: selectedModel,
          max_tokens: maxTokens,
          user_id: auth.currentUser.uid,
          isReasoning: modelDetails?.isReasoning || false,
          isThinking: Boolean(isThinking),
          isDeepResearch: Boolean(isDeepResearch),
          reasoningLevel: reasoningLevel || 'auto',
          chat_id: chatId,
          project_id: projectIdForRequest,
          user_message: backendUserMessagePayload,
          save_user_message: true,
          assistant_message_id: assistantMessageId,
          assistant_message_metadata: {
            modelUsed: selectedModelName,
          },
        };
        onMessageSent({ ...assistantMessage }, chatId);

        const authToken = await auth.currentUser.getIdToken();
        const baseEndpoint = getEndpoint(modelDetails, hasSubscription);
        const finalEndpoint = isThinking && modelDetails?.thinking && isAnthropic(modelDetails)
          ? `${baseEndpoint}-thinking`
          : baseEndpoint;

        const response = await fetch(finalEndpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          let errorMessage = 'Something went wrong. Please try again.';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            // If we can't parse the error response, use the status text
            errorMessage = response.statusText || errorMessage;
          }
          setError(errorMessage);
          assistantMessage.streaming = false;
          assistantMessage.text = errorMessage;
          onMessageSent({ ...assistantMessage }, chatId);
          captureAnalyticsEvent('message_failed', {
            mode: 'single',
            chat_id: chatId,
            model_string: selectedModel,
            model_name: selectedModelName || selectedModel,
            reason: errorMessage,
          });
          return;
        }

        let streamFailed = false;

        try {
          await consumeSseResponse(response, (parsedData) => {
                const {
                  content,
                  reasoning_content,
                  reasoning_summary,
                  citations,
                  meta,
                  error: streamError,
                  image_url: imageUrlFromEvent,
                  imageUrl: imageUrlFromEventAlt,
                  openai_response_id: openaiResponseId,
                  attachment: generatedAttachment,
                  tool_error: toolError,
                  tool_name: toolName,
                } = parsedData;

                // Handle streaming errors
                if (streamError) {
                  if (toolError || toolName === IMAGE_TOOL_NAME) {
                    assistantMessage.streaming = false;
                    assistantMessage.isImageGenerating = false;
                    assistantMessage.imageGenerationStatus = 'error';
                    assistantMessage.toolError = true;
                    assistantMessage.toolName = IMAGE_TOOL_NAME;
                    assistantMessage.text = streamError;
                    onMessageSent({ ...assistantMessage }, chatId);
                  }
                  setError(streamError);
                  captureAnalyticsEvent('message_failed', {
                    mode: 'single',
                    chat_id: chatId,
                    model_string: selectedModel,
                    model_name: selectedModelName || selectedModel,
                    reason: streamError,
                  });
                  streamFailed = true;
                  return false;
                }

                if (meta) {
                  isWebSearching = applyAssistantStreamMeta({
                    meta,
                    assistantMessage,
                    chatId,
                    onMessageSent,
                    isWebSearching,
                    imageToolName: IMAGE_TOOL_NAME,
                  });
                }

                if (openaiResponseId) {
                  assistantMessage.openaiResponseId = openaiResponseId;
                }

                const generatedImageUrl = imageUrlFromEvent || imageUrlFromEventAlt;
                if (generatedImageUrl) {
                  const imageMarkdown = `\n\n![Generated image](<${generatedImageUrl}>)\n\n`;
                  if (!accumulatedResponse.includes(generatedImageUrl)) {
                    accumulatedResponse += imageMarkdown;
                    updateMessage(accumulatedResponse);
                  }
                  assistantMessage.isImageGenerating = false;
                  assistantMessage.imageGenerationStatus = 'completed';
                  assistantMessage.toolError = false;
                  assistantMessage.toolName = IMAGE_TOOL_NAME;
                  assistantMessage.attachments = generatedAttachment
                    ? [generatedAttachment]
                    : [
                        {
                          id: `${assistantMessageId}-generated-image`,
                          type: 'image',
                          name: 'Generated Image',
                          url: generatedImageUrl,
                          previewUrl: generatedImageUrl,
                        },
                      ];
                }

                if (citations && Array.isArray(citations)) {
                  lastCitations = citations;
                }

                if (reasoning_content && !reasoningStartTime) {
                  reasoningStartTime = Date.now();
                }
                if (reasoning_content) {
                  accumulatedReasoning += reasoning_content;
                  assistantMessage.reasoning_text = accumulatedReasoning;
                if (reasoning_summary) {
                  assistantMessage.reasoning_is_summary = true;
                }
                updateMessage(accumulatedResponse, true);
                }

                if (content) {
                  if (reasoningStartTime && !reasoningEndTime) {
                    reasoningEndTime = Date.now();
                  }
                  accumulatedResponse += content;
                  updateMessage(accumulatedResponse);
                }
                return true;
          });
        } catch (error) {
          console.error('Error parsing SSE stream:', error);
          setError('Failed to parse server response. Please try again.');
          captureAnalyticsEvent('message_failed', {
            mode: 'single',
            chat_id: chatId,
            model_string: selectedModel,
            model_name: selectedModelName || selectedModel,
            reason: 'stream_parse_error',
          });
          streamFailed = true;
        }

        if (streamFailed) {
          return;
        }

        let reasoningTime = null;
        if (reasoningStartTime) {
          reasoningTime = (reasoningEndTime || Date.now()) - reasoningStartTime;
        }
        if (reasoningTime != null) {
          assistantMessage.reasoning_time = formatDuration(reasoningTime);
        }

        assistantMessage.streaming = false;
        assistantMessage.text = accumulatedResponse.trim();
        assistantMessage.reasoning_text = accumulatedReasoning.trim();


        if (lastCitations.length > 0) {
          assistantMessage.citations = lastCitations;
        }

        onMessageSent({ ...assistantMessage }, chatId);
        captureAnalyticsEvent('message_completed', {
          mode: 'single',
          response_type: 'text',
          chat_id: chatId,
          model_string: selectedModel,
          model_name: selectedModelName || selectedModel,
          response_char_count: assistantMessage.text.length,
          citation_count: lastCitations.length,
          reasoning_duration_ms: reasoningTime,
        });
        await generateAndUpdateTopic(assistantMessage.text, chatId);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      captureAnalyticsEvent('message_failed', {
        mode: 'single',
        chat_id: chatId || null,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        reason: error.message || 'send_message_exception',
      });
      if (chatId) {
        onMessageSent({ ...userMessage, pending: false, error: true }, chatId);
      }
    }
  }, [
    input,
    attachments,
    activeProjectId,
    selectedChat,
    selectedModel,
    selectedModelName,
    onMessageSent,
    messages,
    hasSubscription,
    navigate,
    isThinking,
    isDeepResearch,
    clearFileState,
    setIsDeepResearch,
    updateChatTitle,
    generateAndUpdateTopic,
    setInput,
    setInputText,
    reasoningLevel,
    getMaxTokens,
    getModelByModelString
  ]);

  const handleSendEditedMessage = useCallback(async (overrideText) => {
    if (!canSendRef.current()) return;
    const editText = typeof overrideText === 'string' ? overrideText : input;
    if (!editingContext || !editText.trim()) return;
    if (!auth.currentUser?.uid) {
      navigate('/welcome');
      return;
    }
    if (!applyEditAndTruncate) return;

    setError(null);

    const chatId = editingContext.chatId || selectedChat?.id;
    if (!chatId) return;
    const projectIdForRequest = getChatProjectId(selectedChat) || activeProjectId || null;

    captureAnalyticsEvent('message_send_requested', {
      mode: 'single',
      chat_id: chatId,
      model_string: selectedModel,
      model_name: selectedModelName || selectedModel,
      attachment_count: 0,
      is_thinking: Boolean(isThinking),
      is_deep_research: Boolean(isDeepResearch),
      is_retry: false,
      is_edit: true,
    });

    try {
      const { truncatedPrimary, updatedMessage } = await applyEditAndTruncate({
        chatId,
        messageId: editingContext.messageId,
        newText: editText,
      });

      const conversationHistory = [...truncatedPrimary];
      const modelDetails = getModelByModelString(selectedModel);
      const maxTokens = getMaxTokens(selectedModel);
      const assistantMessageId = uuid.v4();
      let accumulatedResponse = '';
      let accumulatedReasoning = '';
      let lastCitations = [];
      let isWebSearching = false;
      let reasoningStartTime = null;
      let reasoningEndTime = null;

      const assistantMessage = {
        id: assistantMessageId,
        text: '',
        reasoning_text: '',
        reasoning_pending: expectsReasoning(modelDetails, { isThinking, isDeepResearch, reasoningLevel }),
        reasoning_is_summary: false,
        reasoning_time: '',
        role: 'assistant',
        streaming: true,
        isWebSearching: false,
      };

      const updateMessage = (text, force = false) => {
        const now = Date.now();
        if (force || now - lastUpdateRef.current >= 50) {
          const updated = {
            ...assistantMessage,
            text: text.trim(),
          };
          onMessageSent(updated, chatId);
          lastUpdateRef.current = now;
          assistantMessage.text = text.trim();
        }
      };

      const backendUserMessagePayload = serializeMessageForBackend(updatedMessage);

      const requestBody = {
        messages: conversationHistory,
        model: selectedModel,
        max_tokens: maxTokens,
        user_id: auth.currentUser.uid,
        isReasoning: modelDetails?.isReasoning || false,
        isThinking: Boolean(isThinking),
        isDeepResearch: Boolean(isDeepResearch),
        reasoningLevel: reasoningLevel || 'auto',
        chat_id: chatId,
        project_id: projectIdForRequest,
        user_message: backendUserMessagePayload,
        save_user_message: false,
        assistant_message_id: assistantMessageId,
        assistant_message_metadata: {
          modelUsed: selectedModelName,
        },
      };

      captureAnalyticsEvent('model_used', {
        mode: 'single',
        source: 'edit_resend',
        chat_id: chatId,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        is_thinking: Boolean(isThinking),
        is_deep_research: Boolean(isDeepResearch),
      });

      onMessageSent({ ...assistantMessage }, chatId);

      const authToken = await auth.currentUser.getIdToken();
      const baseEndpoint = getEndpoint(modelDetails, hasSubscription);
      const finalEndpoint = isThinking && modelDetails?.thinking && isAnthropic(modelDetails)
        ? `${baseEndpoint}-thinking`
        : baseEndpoint;

      const response = await fetch(finalEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Something went wrong. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        setError(errorMessage);
        assistantMessage.streaming = false;
        assistantMessage.text = errorMessage;
        onMessageSent({ ...assistantMessage }, chatId);
        captureAnalyticsEvent('message_failed', {
          mode: 'single',
          source: 'edit_resend',
          chat_id: chatId,
          model_string: selectedModel,
          model_name: selectedModelName || selectedModel,
          reason: errorMessage,
        });
        return;
      }

      let streamFailed = false;

      try {
        await consumeSseResponse(response, (parsedData) => {
              const {
                content,
                reasoning_content,
                reasoning_summary,
                citations,
                meta,
                error: streamError,
                image_url: imageUrlFromEvent,
                imageUrl: imageUrlFromEventAlt,
                openai_response_id: openaiResponseId,
                attachment: generatedAttachment,
                tool_error: toolError,
                tool_name: toolName,
              } = parsedData;

              if (streamError) {
                if (toolError || toolName === IMAGE_TOOL_NAME) {
                  assistantMessage.streaming = false;
                  assistantMessage.isImageGenerating = false;
                  assistantMessage.imageGenerationStatus = 'error';
                  assistantMessage.toolError = true;
                  assistantMessage.toolName = IMAGE_TOOL_NAME;
                  assistantMessage.text = streamError;
                  onMessageSent({ ...assistantMessage }, chatId);
                }
                setError(streamError);
                captureAnalyticsEvent('message_failed', {
                  mode: 'single',
                  source: 'edit_resend',
                  chat_id: chatId,
                  model_string: selectedModel,
                  model_name: selectedModelName || selectedModel,
                  reason: streamError,
                });
                streamFailed = true;
                return false;
              }

              if (meta) {
                isWebSearching = applyAssistantStreamMeta({
                  meta,
                  assistantMessage,
                  chatId,
                  onMessageSent,
                  isWebSearching,
                  imageToolName: IMAGE_TOOL_NAME,
                });
              }

              if (openaiResponseId) {
                assistantMessage.openaiResponseId = openaiResponseId;
              }

              const generatedImageUrl = imageUrlFromEvent || imageUrlFromEventAlt;
              if (generatedImageUrl) {
                const imageMarkdown = `\n\n![Generated image](<${generatedImageUrl}>)\n\n`;
                if (!accumulatedResponse.includes(generatedImageUrl)) {
                  accumulatedResponse += imageMarkdown;
                  updateMessage(accumulatedResponse);
                }
                assistantMessage.isImageGenerating = false;
                assistantMessage.imageGenerationStatus = 'completed';
                assistantMessage.toolError = false;
                assistantMessage.toolName = IMAGE_TOOL_NAME;
                assistantMessage.attachments = generatedAttachment
                  ? [generatedAttachment]
                  : [
                      {
                        id: `${assistantMessageId}-generated-image`,
                        type: 'image',
                        name: 'Generated Image',
                        url: generatedImageUrl,
                        previewUrl: generatedImageUrl,
                      },
                    ];
              }

              if (citations && Array.isArray(citations)) {
                lastCitations = citations;
              }

              if (reasoning_content && !reasoningStartTime) {
                reasoningStartTime = Date.now();
              }
              if (reasoning_content) {
                accumulatedReasoning += reasoning_content;
                assistantMessage.reasoning_text = accumulatedReasoning;
              if (reasoning_summary) {
                assistantMessage.reasoning_is_summary = true;
              }
              updateMessage(accumulatedResponse, true);
              }

              if (content) {
                if (reasoningStartTime && !reasoningEndTime) {
                  reasoningEndTime = Date.now();
                }
                accumulatedResponse += content;
                updateMessage(accumulatedResponse);
              }
              return true;
        });
      } catch (error) {
        console.error('Error parsing SSE stream:', error);
        setError('Failed to parse server response. Please try again.');
        captureAnalyticsEvent('message_failed', {
          mode: 'single',
          source: 'edit_resend',
          chat_id: chatId,
          model_string: selectedModel,
          model_name: selectedModelName || selectedModel,
          reason: 'stream_parse_error',
        });
        streamFailed = true;
      }

      if (streamFailed) {
        return;
      }

      let reasoningTime = null;
      if (reasoningStartTime) {
        reasoningTime = (reasoningEndTime || Date.now()) - reasoningStartTime;
      }
      if (reasoningTime != null) {
        assistantMessage.reasoning_time = formatDuration(reasoningTime);
      }

      assistantMessage.streaming = false;
      assistantMessage.text = accumulatedResponse.trim();
      assistantMessage.reasoning_text = accumulatedReasoning.trim();


      if (lastCitations.length > 0) {
        assistantMessage.citations = lastCitations;
      }

      onMessageSent({ ...assistantMessage }, chatId);
      captureAnalyticsEvent('message_completed', {
        mode: 'single',
        source: 'edit_resend',
        response_type: 'text',
        chat_id: chatId,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        response_char_count: assistantMessage.text.length,
        citation_count: lastCitations.length,
        reasoning_duration_ms: reasoningTime,
      });
      await generateAndUpdateTopic(assistantMessage.text, chatId);

      setInput('');
      if (setInputText) setInputText('');
      if (onEditComplete) onEditComplete();
    } catch (error) {
      console.error('Error sending edited message:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      captureAnalyticsEvent('message_failed', {
        mode: 'single',
        source: 'edit_resend',
        chat_id: chatId,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        reason: error.message || 'send_edited_message_exception',
      });
    }
  }, [
    editingContext,
    input,
    applyEditAndTruncate,
    selectedChat,
    activeProjectId,
    selectedModel,
    selectedModelName,
    hasSubscription,
    isThinking,
    isDeepResearch,
    onMessageSent,
    setInput,
    setInputText,
    generateAndUpdateTopic,
    navigate,
    getModelByModelString,
    getMaxTokens,
    reasoningLevel,
    onEditComplete,
  ]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const retryMessage = useCallback(async () => {
    if (!canSendRef.current()) return;
    if (!auth.currentUser?.uid) {
      navigate('/welcome');
      return;
    }

    // Find the last user message from the conversation history
    const lastUserMessage = messages.slice().reverse().find(msg => msg.role === 'user');
    
    if (!lastUserMessage) {
      console.warn('No user message to retry');
      return;
    }

    // Clear error 
    setError(null);

    captureAnalyticsEvent('message_send_requested', {
      mode: 'single',
      chat_id: selectedChat?.id || null,
      model_string: selectedModel,
      model_name: selectedModelName || selectedModel,
      attachment_count: 0,
      is_thinking: Boolean(isThinking),
      is_deep_research: Boolean(isDeepResearch),
      is_retry: true,
      is_edit: false,
    });

    try {
      // Recreate the conversation history exactly as it was when the request failed
      const conversationHistory = [...messages];
      const maxTokens = getMaxTokens(selectedModel);
      const modelDetails = getModelByModelString(selectedModel);
      const retryChatId = selectedChat?.id;
      const projectIdForRequest = getChatProjectId(selectedChat) || activeProjectId || null;
      if (!retryChatId) {
        console.warn('Cannot retry message without an active chat');
        return;
      }
      const assistantMetadata = { modelUsed: selectedModelName };
      const backendUserMessagePayload = serializeMessageForBackend(lastUserMessage);

      captureAnalyticsEvent('model_used', {
        mode: 'single',
        source: 'retry',
        chat_id: retryChatId,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        is_thinking: Boolean(isThinking),
        is_deep_research: Boolean(isDeepResearch),
      });

      // Generate assistant response using the same logic as the original send
      const assistantMessageId = uuid.v4();
      let accumulatedResponse = '';
      let accumulatedReasoning = '';
      let lastCitations = [];
      let isWebSearching = false;

      const assistantMessage = {
        id: assistantMessageId,
        text: '',
        reasoning_text: '',
        reasoning_pending: expectsReasoning(modelDetails, { isThinking, isDeepResearch, reasoningLevel }),
        reasoning_is_summary: false,
        reasoning_time: '',
        role: 'assistant',
        streaming: true,
        isWebSearching: false,
      };

      // Show the typing indicator immediately to simulate a fresh send
      onMessageSent({ ...assistantMessage }, selectedChat?.id);

      const updateMessage = (text, force = false) => {
        const now = Date.now();
        if (force || now - lastUpdateRef.current >= 50) {
          const updatedMessage = {
            ...assistantMessage,
            text: text.trim()
          };
          onMessageSent(updatedMessage, selectedChat?.id);
          lastUpdateRef.current = now;
          assistantMessage.text = text.trim();
        }
      };

        let reasoningStartTime = null;
        let reasoningEndTime = null;

        const requestBody = {
          messages: conversationHistory,
          model: selectedModel,
          max_tokens: maxTokens,
          user_id: auth.currentUser.uid,
          isReasoning: modelDetails?.isReasoning || false,
          isThinking: Boolean(isThinking),
          isDeepResearch: Boolean(isDeepResearch),
          reasoningLevel: reasoningLevel || 'auto',
        };

        if (retryChatId) {
          requestBody.chat_id = retryChatId;
          attachProjectId(requestBody, projectIdForRequest);
          requestBody.user_message = backendUserMessagePayload;
          requestBody.save_user_message = false;
          requestBody.assistant_message_id = assistantMessageId;
          requestBody.assistant_message_metadata = assistantMetadata;
        }

        const authToken = await auth.currentUser.getIdToken();
        const baseEndpoint = getEndpoint(modelDetails, hasSubscription);
        const finalEndpoint = isThinking && modelDetails?.thinking && isAnthropic(modelDetails)
          ? `${baseEndpoint}-thinking`
          : baseEndpoint;

        const response = await fetch(finalEndpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          let errorMessage = 'Something went wrong. Please try again.';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
          setError(errorMessage);
          assistantMessage.streaming = false;
          assistantMessage.text = errorMessage;
          onMessageSent({ ...assistantMessage }, selectedChat?.id);
          captureAnalyticsEvent('message_failed', {
            mode: 'single',
            source: 'retry',
            chat_id: retryChatId,
            model_string: selectedModel,
            model_name: selectedModelName || selectedModel,
            reason: errorMessage,
          });
          return;
        }

        let streamFailed = false;

        try {
          await consumeSseResponse(response, (parsedData) => {
                const {
                  content,
                  reasoning_content,
                  reasoning_summary,
                  citations,
                  meta,
                  error: streamError,
                  image_url: imageUrlFromEvent,
                  imageUrl: imageUrlFromEventAlt,
                  openai_response_id: openaiResponseId,
                  attachment: generatedAttachment,
                  tool_error: toolError,
                  tool_name: toolName,
                } = parsedData;

                // Handle streaming errors
                if (streamError) {
                  if (toolError || toolName === IMAGE_TOOL_NAME) {
                    assistantMessage.streaming = false;
                    assistantMessage.isImageGenerating = false;
                    assistantMessage.imageGenerationStatus = 'error';
                    assistantMessage.toolError = true;
                    assistantMessage.toolName = IMAGE_TOOL_NAME;
                    assistantMessage.text = streamError;
                    onMessageSent({ ...assistantMessage }, selectedChat?.id);
                  }
                  setError(streamError);
                  captureAnalyticsEvent('message_failed', {
                    mode: 'single',
                    source: 'retry',
                    chat_id: retryChatId,
                    model_string: selectedModel,
                    model_name: selectedModelName || selectedModel,
                    reason: streamError,
                  });
                  streamFailed = true;
                  return false;
                }

                if (meta) {
                  isWebSearching = applyAssistantStreamMeta({
                    meta,
                    assistantMessage,
                    chatId: selectedChat?.id,
                    onMessageSent,
                    isWebSearching,
                    imageToolName: IMAGE_TOOL_NAME,
                  });
                }

                if (openaiResponseId) {
                  assistantMessage.openaiResponseId = openaiResponseId;
                }

                const generatedImageUrl = imageUrlFromEvent || imageUrlFromEventAlt;
                if (generatedImageUrl) {
                  const imageMarkdown = `\n\n![Generated image](<${generatedImageUrl}>)\n\n`;
                  if (!accumulatedResponse.includes(generatedImageUrl)) {
                    accumulatedResponse += imageMarkdown;
                    updateMessage(accumulatedResponse);
                  }
                  assistantMessage.isImageGenerating = false;
                  assistantMessage.imageGenerationStatus = 'completed';
                  assistantMessage.toolError = false;
                  assistantMessage.toolName = IMAGE_TOOL_NAME;
                  assistantMessage.attachments = generatedAttachment
                    ? [generatedAttachment]
                    : [
                        {
                          id: `${assistantMessageId}-generated-image`,
                          type: 'image',
                          name: 'Generated Image',
                          url: generatedImageUrl,
                          previewUrl: generatedImageUrl,
                        },
                      ];
                }

                if (citations && Array.isArray(citations)) {
                  lastCitations = citations;
                }

                if (reasoning_content && !reasoningStartTime) {
                  reasoningStartTime = Date.now();
                }
                if (reasoning_content) {
                  accumulatedReasoning += reasoning_content;
                  assistantMessage.reasoning_text = accumulatedReasoning;
                  if (reasoning_summary) {
                    assistantMessage.reasoning_is_summary = true;
                  }
                  updateMessage(accumulatedResponse, true);
                }

                if (content) {
                  if (reasoningStartTime && !reasoningEndTime) {
                    reasoningEndTime = Date.now();
                  }
                  accumulatedResponse += content;
                  updateMessage(accumulatedResponse);
                }
                return true;
          });
        } catch (error) {
          console.error('Error parsing SSE stream:', error);
          setError('Failed to parse server response. Please try again.');
          captureAnalyticsEvent('message_failed', {
            mode: 'single',
            source: 'retry',
            chat_id: retryChatId,
            model_string: selectedModel,
            model_name: selectedModelName || selectedModel,
            reason: 'stream_parse_error',
          });
          streamFailed = true;
        }

        if (streamFailed) {
          return;
        }

        let reasoningTime = null;
        if (reasoningStartTime) {
          reasoningTime = (reasoningEndTime || Date.now()) - reasoningStartTime;
        }
        if (reasoningTime != null) {
          assistantMessage.reasoning_time = formatDuration(reasoningTime);
        }

        assistantMessage.streaming = false;
        assistantMessage.text = accumulatedResponse.trim();
        assistantMessage.reasoning_text = accumulatedReasoning.trim();


        if (lastCitations.length > 0) {
          assistantMessage.citations = lastCitations;
        }

        onMessageSent({ ...assistantMessage }, selectedChat?.id);
        captureAnalyticsEvent('message_completed', {
          mode: 'single',
          source: 'retry',
          response_type: 'text',
          chat_id: retryChatId,
          model_string: selectedModel,
          model_name: selectedModelName || selectedModel,
          response_char_count: assistantMessage.text.length,
          citation_count: lastCitations.length,
          reasoning_duration_ms: reasoningTime,
        });
        await generateAndUpdateTopic(assistantMessage.text, selectedChat?.id);
      
    } catch (error) {
      console.error('Error retrying message:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      captureAnalyticsEvent('message_failed', {
        mode: 'single',
        source: 'retry',
        chat_id: selectedChat?.id || null,
        model_string: selectedModel,
        model_name: selectedModelName || selectedModel,
        reason: error.message || 'retry_message_exception',
      });
    }
  }, [messages, navigate, selectedModel, selectedModelName, selectedChat, activeProjectId, hasSubscription, isThinking, isDeepResearch, reasoningLevel, onMessageSent, setError, lastUpdateRef, generateAndUpdateTopic, getMaxTokens, getModelByModelString]);

  return {
    handleSendMessage,
    handleSendEditedMessage,
    error,
    clearError,
    retryMessage
  };
};
